import { useEffect, useMemo, useRef, useState } from 'react';
import { EA_CLIENT_ID } from '../constants/eaClient';
import {
  loadCorePersonalizationProgress,
  type CorePersonalizationProgress,
} from '../utils/personalizationProgress';

export interface EAPersonalizationData extends CorePersonalizationProgress {
  initiatives: number;
}

type ProgressCacheEntry = {
  clientId: string;
  progress: CorePersonalizationProgress;
};

/** Survives Personalize ↔ Evaluate ↔ Execute remounts. */
let progressCache: ProgressCacheEntry | null = null;
const inflightByClient = new Map<string, Promise<CorePersonalizationProgress>>();
const cacheListeners = new Set<() => void>();

function emitCacheChange(): void {
  cacheListeners.forEach((listener) => listener());
}

function subscribePersonalizationCache(listener: () => void): () => void {
  cacheListeners.add(listener);
  return () => {
    cacheListeners.delete(listener);
  };
}

function pinnedClientId(_maybe?: string): string {
  return EA_CLIENT_ID;
}

/** Clear cached scores so the next hook usage refetches (e.g. after profile/survey/doc edits). */
export function invalidateEAPersonalizationProgressCache(clientId?: string): void {
  const pinned = pinnedClientId(clientId);
  if (!clientId || progressCache?.clientId === String(clientId) || progressCache?.clientId === pinned) {
    progressCache = null;
  }
  inflightByClient.delete(pinned);
  if (clientId) inflightByClient.delete(String(clientId));
  emitCacheChange();
}

function getCachedProgress(clientId: string): CorePersonalizationProgress | null {
  return progressCache?.clientId === clientId ? progressCache.progress : null;
}

function fetchProgress(clientId: string, force: boolean): Promise<CorePersonalizationProgress> {
  if (force) {
    inflightByClient.delete(clientId);
  } else {
    const existing = inflightByClient.get(clientId);
    if (existing) return existing;
  }

  const promise = loadCorePersonalizationProgress(clientId)
    .then((progress) => {
      progressCache = { clientId, progress };
      inflightByClient.delete(clientId);
      emitCacheChange();
      return progress;
    })
    .catch((error) => {
      inflightByClient.delete(clientId);
      throw error;
    });

  inflightByClient.set(clientId, promise);
  return promise;
}

/**
 * Shared Personalization Strength for the EA journey bar + cards.
 *
 * Always loads org EA_CLIENT_ID so Use Case Profile, ROI Model, Scoring Rationale,
 * and the rest of the journey show the same Profile / Surveys / Docs percentages.
 * Cached across step navigation. Pass a new `refreshKey` (or call
 * invalidateEAPersonalizationProgressCache) after profile/survey/doc changes.
 */
export function useEAPersonalizationProgress(
  _clientId?: string,
  initiatives = 0,
  refreshKey = 0,
): {
  personalizationData: EAPersonalizationData;
  progressError: string | null;
  progressLoading: boolean;
} {
  const id = pinnedClientId(_clientId);
  const cachedOnMount = getCachedProgress(id);

  const [progress, setProgress] = useState<CorePersonalizationProgress>(
    () => cachedOnMount ?? { profile: 0, surveys: 0, documents: 0 },
  );
  const [progressError, setProgressError] = useState<string | null>(null);
  const [progressLoading, setProgressLoading] = useState(() => !cachedOnMount);
  const prevRefreshKeyRef = useRef(refreshKey);

  useEffect(() => {
    const refreshRequested = prevRefreshKeyRef.current !== refreshKey;
    prevRefreshKeyRef.current = refreshKey;

    const cached = getCachedProgress(id);
    if (cached && !refreshRequested) {
      setProgress(cached);
      setProgressError(null);
      setProgressLoading(false);
      return;
    }

    const coldStart = !cached;
    if (coldStart) setProgressLoading(true);

    let cancelled = false;
    fetchProgress(id, refreshRequested)
      .then((p) => {
        if (cancelled) return;
        setProgress(p);
        setProgressError(null);
      })
      .catch((err) => {
        if (cancelled) return;
        if (coldStart) {
          setProgress({ profile: 0, surveys: 0, documents: 0 });
        }
        setProgressError(err instanceof Error ? err.message : 'Failed to load personalization progress.');
      })
      .finally(() => {
        if (!cancelled) setProgressLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [id, refreshKey]);

  useEffect(() => {
    let alive = true;
    const unsubscribe = subscribePersonalizationCache(() => {
      if (!alive) return;
      const cached = getCachedProgress(id);
      if (cached) {
        setProgress(cached);
        setProgressError(null);
        setProgressLoading(false);
        return;
      }
      setProgressLoading(true);
      fetchProgress(id, true)
        .then((p) => {
          if (!alive) return;
          setProgress(p);
          setProgressError(null);
        })
        .catch((err) => {
          if (!alive) return;
          setProgressError(err instanceof Error ? err.message : 'Failed to load personalization progress.');
        })
        .finally(() => {
          if (alive) setProgressLoading(false);
        });
    });
    return () => {
      alive = false;
      unsubscribe();
    };
  }, [id]);

  const personalizationData = useMemo(
    (): EAPersonalizationData => ({
      ...progress,
      // Hold initiatives out of the average on cold start only — otherwise
      // (0+0+0+100)/4 flashes as 25% while profile/surveys/docs are still fetching.
      initiatives: progressLoading ? 0 : initiatives,
    }),
    [progress, initiatives, progressLoading],
  );

  return { personalizationData, progressError, progressLoading };
}
